/**
 * Состав дивизиона: объекты с показателями заполненности.
 *
 * Открывается по выбору дивизиона в рейтинге. Отвечает на вопрос
 * «что внутри и за что браться», поэтому объекты по умолчанию
 * упорядочены от худших по заполненности.
 */
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

import { selectGrouped, selectPrefixMap } from '../../store/slices/switchesSlice';
import { selectDivisions } from '../../store/slices/divisionsSlice';
import { getDivisionStats, hasNoLyra, percent } from '../../domain/divisions';
import { objectLabel } from '../../domain/issues';
import { plural } from '../../domain/plural';
import PageHeader from '../../components/common/PageHeader';
import ui from '../../components/common/ui.module.css';
import styles from './DivisionDetail.module.css';

export default function DivisionDetail() {
  const { name } = useParams();
  const navigate = useNavigate();

  const grouped = useSelector(selectGrouped);
  const divisionMap = useSelector(selectDivisions);
  const prefixMap = useSelector(selectPrefixMap);

  const [sort, setSort] = useState('completion');

  const division = decodeURIComponent(name || '');
  const mags = divisionMap[division];

  const stats = useMemo(
    () => (mags ? getDivisionStats(division, mags, grouped) : null),
    [division, mags, grouped]
  );

  const objects = useMemo(() => {
    if (!mags) return [];
    const list = mags.map((mag) => {
      const rows = grouped[mag] ?? [];
      const issues = rows.filter((r) => r._issues > 0).length;
      const noLyra = rows.filter(hasNoLyra).length;
      return {
        mag,
        label: objectLabel(mag, prefixMap),
        total: rows.length,
        issues,
        noLyra,
        completion: percent(rows.length - issues, rows.length),
      };
    });

    // По умолчанию — от худших: раздел нужен, чтобы понять,
    // где хуже всего, а не пролистывать благополучные объекты.
    if (sort === 'completion') return list.sort((a, b) => a.completion - b.completion);
    if (sort === 'noLyra') return list.sort((a, b) => b.noLyra - a.noLyra);
    if (sort === 'size') return list.sort((a, b) => b.total - a.total);
    return list.sort((a, b) => a.label.localeCompare(b.label, 'ru', { numeric: true }));
  }, [mags, grouped, prefixMap, sort]);

  if (!mags) {
    return (
      <div className={styles.page}>
        <PageHeader
          title={`Дивизион «${division}» не найден`}
          subtitle="Возможно, его переименовали или удалили."
        />
        <div className={styles.inner}>
          <button className={ui.button} onClick={() => navigate('/divisions')}>
            К списку дивизионов
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.backRow}>
        <button className={styles.back} onClick={() => navigate('/divisions')}>
          ← Все дивизионы
        </button>
      </div>
      <PageHeader
        title={division}
        subtitle={`${stats.mags.length} ${plural(stats.mags.length, 'объект', 'объекта', 'объектов')}, ${stats.total} ${plural(stats.total, 'коммутатор', 'коммутатора', 'коммутаторов')}`}
      />

      <section className={styles.overview} aria-label="Заполненность дивизиона">
        <div className={styles.overviewMain}>
          <span className={styles.bigValue}>{String(stats.completion).replace('.', ',')} %</span>
          <span className={styles.bigLabel}>коммутаторов без замечаний</span>
          <span className={styles.track}>
            <span className={styles.fill} style={{ width: `${stats.completion}%` }} />
          </span>
        </div>
        <dl className={styles.facts}>
          <div>
            <dt>Коммутаторов</dt>
            <dd>{stats.total}</dd>
          </div>
          <div>
            <dt>С замечаниями</dt>
            <dd className={stats.issues ? styles.warn : ''}>{stats.issues}</dd>
          </div>
          <div>
            <dt>Без SN Lyra</dt>
            <dd className={stats.noLyra ? styles.warn : ''}>{stats.noLyra}</dd>
          </div>
        </dl>
      </section>

      <div className={styles.inner}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Объекты дивизиона</h2>
          <label className={styles.sortLabel}>
            Порядок
            <select
              className={styles.select}
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="completion">Сначала худшие по заполненности</option>
              <option value="noLyra">Сначала с наибольшим числом без SN Lyra</option>
              <option value="size">Сначала крупные</option>
              <option value="name">По названию</option>
            </select>
          </label>
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Объект</th>
                <th className={styles.barCol}>Заполненность</th>
                <th className={styles.num}>Коммутаторов</th>
                <th className={styles.num}>С замечаниями</th>
                <th className={styles.num}>Без SN Lyra</th>
              </tr>
            </thead>
            <tbody>
              {objects.map((item) => {
                const open = () => navigate(`/objects/${item.mag}`);
                return (
                  <tr
                    key={item.mag}
                    onClick={open}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') open();
                    }}
                  >
                    <td className={styles.name}>{item.label}</td>
                    <td className={styles.barCol}>
                      <span className={styles.completion}>
                        <span className={styles.track}>
                          <span className={styles.fill} style={{ width: `${item.completion}%` }} />
                        </span>
                        <span className={styles.pct}>
                          {String(item.completion).replace('.', ',')} %
                        </span>
                      </span>
                      {/* На телефоне столбцы с числами скрыты — те же числа строкой */}
                      <span className={styles.meta}>
                        {item.total}{' '}
                        {plural(item.total, 'коммутатор', 'коммутатора', 'коммутаторов')}
                        {' · '}
                        <span className={item.issues ? styles.warn : ''}>
                          {item.issues} с замечаниями
                        </span>
                        {' · '}
                        <span className={item.noLyra ? styles.warn : ''}>
                          {item.noLyra} без SN Lyra
                        </span>
                      </span>
                    </td>
                    <td className={styles.num}>{item.total}</td>
                    <td className={`${styles.num} ${item.issues ? styles.warn : ''}`}>
                      {item.issues}
                    </td>
                    <td className={`${styles.num} ${item.noLyra ? styles.warn : ''}`}>
                      {item.noLyra}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
